/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { ThemeProvider } from "@cd2/theme"
import { AlertProvider } from "@cd2/components"

export const brandColors = {
  mint: "#00deba",
  night: "#212d40",
  mintPop: "#c7ffda",
  emerald: "#07a394",
  darkMint: "#00bca8",
  lightMint: "#60e6cb",
  mintBackground: "#CDE8D0",
  darkNight: "#001d30",
  lightNight: "#4a4e69",
  lighterNight: "#979bb5",
  midGrey: "#e9e4e9",
  lightGrey: "#f7f8f9",
  white: "#ffffff",
  blaze: "#ff2145",
  customersNight: "#242f3e",
  customersPink: "#df81c6",
  altPink: "#df86c9",

  //

  lightestMint: "#ecfaf1",

  orange: "#de9800",
  red: "#de0000",

  redLightBackground: "#FFC8D1",
  orangeLightBackground: "#F4EBCE",
  blueLightBackground: "#D5DAFE",
  greenLightBackground: "#D4FEE2",
  greyLightBackground: "#E8E3E8",
  archived: "#fffbd2",
}

export const externalColors = {
  xls: "#1d6f42",
  pdf: "#B30B00",
  sage: "#007e65",
}

export const themeColors = {
  blue: "rgb(45,62,142)",
  lightestBlue: "#edf7ff",
  lightBlue: "#a5d4f3",
  darkestBlue: "#242f3e",
  orange: "#e67e22",
  lightOrange: "#ffd6a4",
  lightGreen: "#eafff2",
  lightGrey: "#e1e6ea",
  grey: "#8c8f92",
  darkGrey: "#4d4d51",
  lightestGrey: "#faf9f8",
  lightRed: "#ffcecb",
  alertRed: "#ff313e",
  red: "#ac242d",
  darkRed: "#922b20",
  green: "#27ae60",
  pdfRed: "#f40f02",
  xlsGreen: "#1d6f42",
}
export const colors = {
  default: brandColors.mint,
  primary: brandColors.mint,
  warningNotice: themeColors.lightOrange,
  infoNotice: brandColors.blueLightBackground,
  errorNotice: themeColors.lightRed,

  lightBorder: "#c5c9cd",

  addresses: "#e74c3c",
  deliveries: "#27ae60",
  orders: "#3498db",
  quotes: "#e67e22",
  drivers: "#8e44ad",
  customers: "#51ad97",
  invoices: "#2c3e50",
  purchaseOrders: "#2980b9",
  users: "#8e44ad",
  usersBlue: "#2c4192",
  branches: "#c94436",
  jobs: "#7379a7",
  clients: "#7379a7",
  sage: "#007e65",
  notifications: "#4ab988",
  stock: "#1a5736",
  supportAndReturns: "#1a5736",
}

/** The app's font, for components that style text outside the @cd2 theme */
export const appFontFamily = "'Space Grotesk', 'sans-serif'"

export function EOSThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider
      theme={{
        fontFamily: appFontFamily,
        sizes: { large: 24, medium: 12, small: 6, default: 12 },
        colors,
        borderRadius: 4,
        text: {
          blockSpacing: 0.25,
          //https://grtcalculator.com/ (based on 14px base)
          ratios: { "1": 2, "2": 1.643, "3": 1.286, "4": 1, p: 1 },
          sizes: { large: 112.5, normal: 87.5, small: 75 },
          groups: { bodyColor: brandColors.night, preColor: brandColors.lightNight },
          lineHeight: 1.25,
          colors: {
            default: brandColors.night,
          },
        },
        forms: {
          font: {
            size: isMobile ? 16 : 14,
          },
          border: {
            color: "#999",
            hoverColor: "rgb(33,33,31)",
          },
          padding: 9,
          placeholderColor: "#d1d1d1",
          label: {
            font: {
              size: 13,
              color: brandColors.lightNight,
            },
            spacing: 2,
          },
        },
        button: {
          boxShadow: "0 0 0 1px",
          weight: 700,
          padding: { topAndBottom: 10, sides: 14 },
          fontSize: 14,
          defaultButtonColor: brandColors.darkMint,
        },
        alert: {
          boxShadow: "none",
        },
        boxShadow: {
          xs: "0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.20)",
          sm: "0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.12), 0 1px 5px 0 rgba(0,0,0,0.20)",
          md: "0 3px 4px 0 rgba(0,0,0,0.14), 0 3px 3px -2px rgba(0,0,0,0.12), 0 1px 8px 0 rgba(0,0,0,0.20)",
          lg: "0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12), 0 2px 4px -1px rgba(0,0,0,0.20)",
          xl: "0 6px 10px 0 rgba(0,0,0,0.14), 0 1px 18px 0 rgba(0,0,0,0.12), 0 3px 5px -1px rgba(0,0,0,0.20)",
          xxl: "0 8px 10px 1px rgba(0,0,0,0.14), 0 3px 14px 2px rgba(0,0,0,0.12), 0 5px 5px -3px rgba(0,0,0,0.20)",
          inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
        },
        table: {
          headings: {
            fontSize: 11,
            padding: "2px 10px",
          },
          cells: {
            padding: "8px 10px",
            fontSize: 13,
          },
          cssHeadingRow: css`
            th {
              white-space: nowrap;
              border-bottom: 1px solid ${brandColors.midGrey};
            }
          `,
          cssRow: css`
            border-left: 1px solid ${brandColors.midGrey};
            border-right: 1px solid ${brandColors.midGrey};
            border-bottom: 1px solid ${brandColors.midGrey};

            td {
              height: 50px;
            }
          `,
        },
      }}
      globalStyles={css`
        body {
          color: ${brandColors.night};
        }

        img,
        svg {
          max-width: 100%;
        }

        a {
          color: ${brandColors.emerald};
          text-decoration: none;
        }

        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        ul {
          margin: 0;
          padding: 0 0 0 18px;
          color: rgba(0, 0, 0, 0.83);
          line-height: 1.5;
        }

        .react-datepicker__day--selected {
          color: ${colors.primary} !important;
        }

        .tippy-box[data-animation="fade"][data-state="hidden"] {
          opacity: 0;
        }

        [data-tippy-root] {
          max-width: calc(100vw - 10px);
        }

        .tippy-box {
          transition-property: transform, visibility, opacity;
        }

        .tippy-box[data-placement^="top"] > .tippy-arrow {
          bottom: 0;
        }

        .tippy-box[data-placement^="top"] > .tippy-arrow:before {
          bottom: -7px;
          left: 0;
          border-width: 8px 8px 0;
          border-top-color: initial;
          transform-origin: center top;
        }

        .tippy-box[data-placement^="bottom"] > .tippy-arrow {
          top: 0;
        }

        .tippy-box[data-placement^="bottom"] > .tippy-arrow:before {
          top: -7px;
          left: 0;
          border-width: 0 8px 8px;
          border-bottom-color: initial;
          transform-origin: center bottom;
        }

        .tippy-box[data-placement^="left"] > .tippy-arrow {
          right: 0;
        }

        .tippy-box[data-placement^="left"] > .tippy-arrow:before {
          border-width: 8px 0 8px 8px;
          border-left-color: initial;
          right: -7px;
          transform-origin: center left;
        }

        .tippy-box[data-placement^="right"] > .tippy-arrow {
          left: 0;
        }

        .tippy-box[data-placement^="right"] > .tippy-arrow:before {
          left: -7px;
          border-width: 8px 8px 8px 0;
          border-right-color: initial;
          transform-origin: center right;
        }

        .tippy-box[data-inertia][data-state="visible"] {
          transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
        }

        .tippy-arrow {
          width: 16px;
          height: 16px;
          color: #ffffff;
        }

        .tippy-arrow:before {
          content: "";
          position: absolute;
          border-color: transparent;
          border-style: solid;
        }

        .tippy-content {
          position: relative;
          z-index: 1;
        }

        .tippy-box[data-animation="scale"][data-placement^="top"] {
          transform-origin: bottom;
        }

        .tippy-box[data-animation="scale"][data-placement^="bottom"] {
          transform-origin: top;
        }

        .tippy-box[data-animation="scale"][data-placement^="left"] {
          transform-origin: right;
        }

        .tippy-box[data-animation="scale"][data-placement^="right"] {
          transform-origin: left;
        }

        .tippy-box[data-animation="scale"][data-state="hidden"] {
          transform: scale(0.5);
          opacity: 0;
        }

        &.tippy-box[data-animation="perspective"][data-placement^="bottom"] {
          transform-origin: top;
        }

        &.tippy-box[data-animation="perspective"][data-placement^="bottom"][data-state="visible"] {
          transform: perspective(700px);
        }

        &.tippy-box[data-animation="perspective"][data-placement^="bottom"][data-state="hidden"] {
          transform: perspective(700px) translateY(-8px) rotateX(-60deg);
        }

        &.tippy-box[data-animation="perspective"][data-state="hidden"] {
          opacity: 0;
        }

        //TODO REMOVE!
        fieldset {
          padding: 16px;
          border: 1px solid rgba(0, 0, 0, 0.15);
          border-radius: 4px;
        }

        legend {
          font-size: 12px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          border-radius: 4px 4px 0 0;
        }

        .headingLayout {
          display: flex;

          > div {
            flex: 1;
          }
        }

        .actions {
          text-align: right;
          margin-bottom: 16px;

          .cd2-list--horizontal {
            justify-content: flex-end;
          }
        }

        .cd2-tableBody tr.prices-row {
          td {
            border-bottom: none !important;
            padding: 6px;
            background: #f1f1f1;
            font-size: 12px;
          }
        }

        .flex-row {
          display: flex;
          align-items: stretch;

          > * {
            flex: 1;
          }

          > * + * {
            margin-left: $normalGutter;
          }
        }

        label {
          font-size: 12px;
        }

        .SingleDatePickerInput__showClearDate {
          padding-right: 0 !important;
        }

        .cd2-form-input,
        .DateInput_input,
        .cd2-field-select__value-container {
          padding-top: 8px !important;
          padding-bottom: 8px !important;
          font-size: 13px;
        }

        .cd2-form-input,
        .DateInput_input {
          border: 1px solid rgba(0, 0, 0, 0.25);
          box-shadow: none;
          background: white;

          &:hover,
          &:focus {
            border: 1px solid #55a3da;
          }

          &.cd2-form-input--errored {
            border: 1px solid #b42f32;
          }
        }

        .cd2-field-select__control {
          border: 1px solid rgba(0, 0, 0, 0.25);
          box-shadow: none;
          background: white;

          &:hover,
          &:focus {
            border: 1px solid #55a3da;
          }
        }

        .cd2-form-valid-check {
          top: 19px;
        }

        input {
          accent-color: ${colors.primary};
        }

        tr.badge-error {
          background: ${colors.errorNotice};
        }

        tr.badge-warning {
          background: ${brandColors.orangeLightBackground};
        }

        tr.badge-info {
          background: ${colors.infoNotice};
        }
      `}
    >
      <AlertProvider
        css={css`
          > * > * {
            padding: 0;
          }
        `}
      >
        {children}
      </AlertProvider>
    </ThemeProvider>
  )
}

export const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
  navigator.userAgent,
)
